/**
 * "Backups" gadget (Operate): the project's restore points of its data (vfs-service `GET /data/restore-points`: automatic,
 * by hand, before a change, from a backup) with the newest first, and the sandbox's nightly backups (`GET /data/backups`).
 * "Save a restore point" (the Data tab's own dialog, `POST /data/restore-points`) for an editor; a viewer only reads. Restore points
 * not available (no runner, an older one): its note is shown as it is.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SaveRestorePointDialog } from '../../data/RestorePoints';
import { ago } from '../../../utils/blameModel';
import { formatBytes } from '../../../utils/dataModel';
import { backupSummary } from '../../../utils/homeGadgets/operate';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useBackups, useRestorePoints } from './sources';

const KIND = { auto: 'Automatic', manual: 'By hand', 'pre-change': 'Before a change', backup: 'From a backup' };
const SHOWN = 3;

export default function BackupsGadget({ projectId, canEdit = false, onOpenData = null }) {
  const points = useRestorePoints(projectId);
  const backups = useBackups(projectId);
  const summary = backupSummary({ points: points.data, backups: backups.data });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const nightly = summary.nightly;
  return (
    <GadgetCard links={<TabLink onClick={onOpenData}>Build › Data</TabLink>} meta={summary.last ? `last ${ago(summary.last.createdAt)}` : null} testId="backups-gadget" title="Backups">
      <GadgetBody
        data={points.data}
        empty={summary.available === false}
        emptyText={summary.reason || 'Restore points are not available in this sandbox.'}
        error={points.error}
        errorText="The restore points could not be read just now."
        loading={points.loading}
        loadingText="Reading the restore points…"
      >
        {() => (
          <>
            {summary.points.length === 0 ? (
              <p className="ph-quiet">No restore point yet.</p>
            ) : (
              <ul className="gd-rows">
                {summary.points.slice(0, SHOWN).map((p) => (
                  <li className="gd-row" data-testid={`backup-${p.id}`} key={p.id}>
                    <strong>{p.name || KIND[p.kind] || 'Restore point'}</strong>
                    <span className="gd-grow ph-muted">{KIND[p.kind] || p.kind}{p.sizeBytes ? ` · ${formatBytes(p.sizeBytes)}` : ''}</span>
                    <span className="ph-muted ph-small">{ago(p.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="gd-note" data-testid="backups-nightly">
              {nightly.available === false ? 'Nightly backups: not available in this sandbox.'
                : nightly.last ? `Nightly backups: ${nightly.count}, the last ${ago(nightly.last.createdAt)}.`
                  : backups.error && !backups.data ? 'Nightly backups could not be read just now.'
                    : backups.data ? 'Nightly backups: none yet.' : 'Reading the nightly backups…'}
            </p>
            {canEdit && (
              <div className="gd-actions">
                <button className="ph-btn ph-btn-small" data-testid="backups-save" onClick={() => { setSaved(false); setSaving(true); }} type="button">Save a restore point</button>
              </div>
            )}
            {saved && <p className="gd-note" role="status">Restore point saved.</p>}
            {saving && (
              <SaveRestorePointDialog onClose={() => setSaving(false)} onSaved={() => { setSaved(true); points.refresh(); }} projectId={projectId} />
            )}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

BackupsGadget.propTypes = { canEdit: PropTypes.bool, onOpenData: PropTypes.func, projectId: PropTypes.string.isRequired };
